import { TICKET_STATUS_ESCALATED } from 'constants/ticketStatuses';
import { isSpecialist } from 'extractFromState';
import { setChatAlertActiveId, removeTicketFromQueue, selectTicket, addTicketToQueue } from 'actions';
import { updateTicketInQueue } from 'actions/ticketActions';
import { liveQuerySubscribeLock, LIVE_QUERY_SUBSCRIBE_LOCK_KEY } from 'util/asyncLocks';
import * as SessionAnalyticsService from 'services/SessionAnalyticsService/SessionAnalyticsService';
import * as tabs from 'constants/tabNames';
import {
  EXPERT_QUEUE_CREATE_SUBSCRIPTION,
  EXPERT_QUEUE_ENTER_SUBSCRIPTION,
  EXPERT_QUEUE_UPDATE_SUBSCRIPTION,
  EXPERT_QUEUE_LEAVE_SUBSCRIPTION,
} from '../expertQueue/expertQueueSubscriptions.gql';

import { getExpertId } from 'util/parseSelectors';
import { getLockedConversationId, extractSelectedInboxTab, getChatAlertActiveId } from 'extractFromState';
import { chooseDefaultTicket } from 'util/chooseDefault';
import subscribeToLiveQuery from '../subscribeToLiveQuery';
import isEmpty from 'lodash/isEmpty';
import { logMessage } from '../../services/ErrorService/ErrorService';

const nominatedQueueSubscriptions = [];

// This is mainly for unit tests
const resetNominatedQueueSubscriptions = () => {
  // Remove all elements from validateIntentTicketSubscriptions
  nominatedQueueSubscriptions.length = 0;
};

// returns a promise that resolves when connection is open
const startNominatedLiveQuery = (store) => {
  // expert query gives us an expertQueue object while specialist query gives us conversation
  const getConversationFromQueueOrConversation = (expertQueueOrConversation) => {
    return isSpecialist(store.getState()) ? expertQueueOrConversation : expertQueueOrConversation.conversationPtr;
  };

  const isQueueOrConversationActive = (expertQueueOrConversation) => {
    if (isSpecialist(store.getState())) {
      // if admin, we are active if the ticket status is escalated
      return expertQueueOrConversation.status === TICKET_STATUS_ESCALATED;
    }

    // if expert, check if expert queue is active
    return expertQueueOrConversation.isActive;
  };

  const getNominationExpiresAt = (expertQueueOrConversation) => {
    if (isSpecialist(store.getState())) {
      throw new Error('Specialist escalations do not have nominationExpiresAt');
    } else {
      return expertQueueOrConversation.nominationExpiresAt;
    }
  };

  return new Promise((resolve) => {
    /*
     * Starting Nominated Ticket Queue (Incoming) live query.
     * Should not start it for specialist - as specialist already has ESCALATED live query.
     * Shouldn't start live query if it has already been started.
     */
    if (!isEmpty(nominatedQueueSubscriptions) || isSpecialist(store.getState())) {
      return resolve();
    }

    liveQuerySubscribeLock.acquire(LIVE_QUERY_SUBSCRIBE_LOCK_KEY, async (lockDoneFunction) => {
      try {
        const expertId = getExpertId();

        if (!expertId) {
          return;
        }

        const variables = {
          filter: {
            simplrExpertId: {
              eq: expertId,
            },
            isActive: {
              is: true,
            },
          },
        };

        const handleOnCreate = async (expertQueueOrConversation) => {
          // @TODO: Verifying whether these calls are also being made.
          SessionAnalyticsService.event('[GQL] Nominated CREATED - Event Received', {});
          const conversation = getConversationFromQueueOrConversation(expertQueueOrConversation);

          /*
           *  This event will be triggered when an Expert is nominated for an open/new ticket.
           *  Experts are considered for nomination only when they are logged in and their nominatedMessageCount < nominatedMax
           *
           *  Note: There is no need as such to delay the execution of this event as it can't be triggered on any of the Expert's
           *  own UI action unlike Assigned Ticket Live Query create/enter & leave on "Take" & "Resolve"/"Escalate".
           */

          SessionAnalyticsService.event('Expert Nominated', {
            ticket: conversation.objectId,
          });

          let nominatedTickets = store.getState().tickets.nominatedTickets;
          if (!nominatedTickets[conversation.objectId] && conversation?.customerPtr) {
            await store.dispatch(
              addTicketToQueue({
                conversation,
                queueName: tabs.NOMINATED,
                nominationExpiresAt: getNominationExpiresAt(expertQueueOrConversation),
                showNotification: true,
              })
            );
          }
        };

        const handleOnUpdate = (expertQueueOrConversation) => {
          const conversation = getConversationFromQueueOrConversation(expertQueueOrConversation);

          // NOTE(Bohdan|SPD-12311|2025-05-26): Sometimes during test runs, QA engineers encounter errors where customerPtr is missing.
          // This blocks them from successfully forwarding and assigning tickets to experts. The issue has been observed in the QA environment
          // and is tricky to reproduce. However, there's no mention of this occurring in production. After debugging, it was noticed that
          // after subscribing to a live query, we receive a conversation object that includes only the id field — all other fields are null.
          // This seems to be the root cause of the issue on the frontend side. The assumption is that the simplr-platform-data-layer
          // has a concurrency issue and might be missing some locks. QA engineers have reported that the issue is very flaky and tends
          // to occur when a large number of tickets are sent during a test run. The goal of the check below is to prevent propagating
          // ticket updates with incomplete or incorrect data. However, this solution may need to be revisited on the simplr-platform-data-layer side.
          // Example of the conversation object that causes the issue:
          // {"id":"PPHyAaDqwe","objectId":"PPHyAaDqwe","createdAt":null,"updatedAt":null, ...other fields are null, only id and objectId are present}
          if (!conversation.customerPtr) {
            logMessage('Skip updating ticket in queue since customerPtr is missing');

            return;
          }

          /*
           *  TODO: This event may not be needed anymore as Expert Queue row is updated only when isActive flag has to be toggled which is captured by "leave" event.
           */
          store.dispatch(updateTicketInQueue(conversation, 'nominated', isSpecialist(store.getState())));
        };

        const handleOnEnter = (expertQueueOrConversation) => {
          // @TODO: Verifying whether these calls are also being made.
          SessionAnalyticsService.event('[GQL] Nominated ENTER - Event Received', {});
          const conversation = getConversationFromQueueOrConversation(expertQueueOrConversation);

          /*
           *  This event will be triggered when an Expert is nominated for an open/new ticket.
           *  Experts are considered for nomination only when they are logged in and their nominatedMessageCount < nominatedMax
           *
           *  Note: There is no need as such to delay the execution of this event as it can't be triggered on any of the Expert's
           *  own UI action unlike Assigned Ticket Live Query create/enter & leave on "Take" & "Resolve"/"Escalate".
           */

          SessionAnalyticsService.event('Expert Nominated', {
            ticket: conversation.objectId,
          });

          if (isQueueOrConversationActive(expertQueueOrConversation)) {
            // No need to do this check as the Live Query definition itself includes query.equalTo("isActive", true)
            let nominatedTickets = store.getState().tickets.nominatedTickets;
            if (!nominatedTickets[conversation.objectId]) {
              store.dispatch(
                addTicketToQueue({
                  conversation,
                  queueName: tabs.NOMINATED,
                })
              );
            }
          }
        };

        const handleOnLeave = (expertQueueOrConversation) => {
          const conversation = getConversationFromQueueOrConversation(expertQueueOrConversation);

          /*
           *  This event will be triggered when an Expert is no longer nominated for the Ticket.
           *  Possible use cases: 1) When expert assigns the ticket to herself. Ticket moves from Nominated to Assigned status.
           *                      2) When the time for which Expert has been nominated for a particular ticket exceeds Nomination Timeout. (Not implemented yet)
           */
          let nominatedTickets = store.getState().tickets.nominatedTickets;
          const isConversationLocked = getLockedConversationId(store.getState()) === conversation.objectId;
          if (nominatedTickets[conversation.objectId] && !isConversationLocked) {
            const chatAlertActiveId = getChatAlertActiveId(store.getState());
            if (chatAlertActiveId === conversation.objectId) {
              // if ticket had an open alert notification, close it
              store.dispatch(setChatAlertActiveId(null, { forceOverride: true }));
            }

            store.dispatch(
              removeTicketFromQueue(conversation.objectId, tabs.NOMINATED, isSpecialist(store.getState()))
            );

            const selectedTicketId = store.getState().selectedTicketId;
            if (selectedTicketId === conversation.objectId) {
              const newSelectedTicketId = chooseDefaultTicket('all', store.getState());
              const selectedInboxTab = extractSelectedInboxTab(store.getState());
              if (newSelectedTicketId && selectedInboxTab === tabs.NOMINATED) {
                store.dispatch(selectTicket(newSelectedTicketId, tabs.NOMINATED));
              }
            }
          }
        };

        const createSubscription = subscribeToLiveQuery(
          EXPERT_QUEUE_CREATE_SUBSCRIPTION,
          variables,
          `onCreateExpertQueue`,
          handleOnCreate
        );

        const updateSubscription = subscribeToLiveQuery(
          EXPERT_QUEUE_UPDATE_SUBSCRIPTION,
          variables,
          `onUpdateExpertQueue`,
          handleOnUpdate
        );

        const enterSubscription = subscribeToLiveQuery(
          EXPERT_QUEUE_ENTER_SUBSCRIPTION,
          variables,
          `onEnterExpertQueue`,
          handleOnEnter
        );

        const leaveSubscription = subscribeToLiveQuery(
          EXPERT_QUEUE_LEAVE_SUBSCRIPTION,
          variables,
          `onLeaveExpertQueue`,
          handleOnLeave
        );

        nominatedQueueSubscriptions.push(createSubscription, updateSubscription, enterSubscription, leaveSubscription);
      } finally {
        lockDoneFunction();
        resolve();
      }
    });
  });
};

export { startNominatedLiveQuery, resetNominatedQueueSubscriptions };
